import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import {
  getCollections,
  updateCollectionCase,
} from "../services/crmCollectionsService";
import {
  autoCreateOverdueCollectionCases,
  markCollectionResolved,
} from "../services/crmCollectionsAutomationService";
import "../styles/crm.css";

const CollectionsPage = () => {
  const { companyId } = useCompany();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    if (!companyId) {
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const data = await getCollections(companyId);
      setRows(data);
    } catch (error) {
      setMessage(error.message || "Failed to load collections");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAutoCreate = useCallback(async () => {
    if (!companyId) {
      return;
    }

    setMessage("");

    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError) {
        throw userError;
      }

      const created = await autoCreateOverdueCollectionCases(
        companyId,
        user?.id || null
      );
      await load();
      setMessage(`${created.length} collection case(s) created.`);
    } catch (error) {
      setMessage(error.message || "Failed to auto-create collection cases");
    }
  }, [companyId, load]);

  const handleStatusChange = useCallback(
    async (collectionId, status) => {
      if (!companyId || !collectionId) {
        return;
      }

      setSavingId(collectionId);
      setMessage("");

      try {
        if (status === "resolved") {
          await markCollectionResolved(companyId, collectionId);
        } else {
          await updateCollectionCase(collectionId, companyId, {
            status,
            last_contact_at: new Date().toISOString(),
          });
        }

        await load();
      } catch (error) {
        setMessage(error.message || "Failed to update collection case");
      } finally {
        setSavingId("");
      }
    },
    [companyId, load]
  );

  return (
    <div>
      <h2 className="crm-page-title">Collections</h2>

      {message ? <div className="crm-message">{message}</div> : null}

      <div className="crm-inline-actions" style={{ marginBottom: "16px" }}>
        <button className="crm-primary-btn" onClick={handleAutoCreate}>
          Auto-Create Overdue Cases
        </button>
      </div>

      {loading ? (
        <div className="crm-message">Loading collections...</div>
      ) : rows.length === 0 ? (
        <div className="crm-message">No collection cases found.</div>
      ) : (
        rows.map((row) => (
          <div key={row.id} className="crm-list-card">
            <h3 className="crm-list-title">
              {row.crm_accounts?.name || "No account"}
            </h3>
            <div className="crm-list-subtext">Status: {row.status}</div>
            <div className="crm-list-subtext">
              Priority: {row.priority || "medium"}
            </div>
            <div className="crm-list-subtext">
              Auto Created: {row.auto_created ? "Yes" : "No"}
            </div>
            <div className="crm-list-subtext">
              Next follow-up:{" "}
              {row.next_follow_up_at
                ? new Date(row.next_follow_up_at).toLocaleString()
                : "Not set"}
            </div>
            <div className="crm-list-subtext">{row.note || ""}</div>

            <div className="crm-inline-actions">
              <select
                className="crm-select"
                value={row.status}
                onChange={(event) =>
                  handleStatusChange(row.id, event.target.value)
                }
                disabled={savingId === row.id}
                style={{ maxWidth: "220px" }}
              >
                <option value="open">Open</option>
                <option value="promise_to_pay">Promise to pay</option>
                <option value="disputed">Disputed</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default CollectionsPage;
